(() => {
  if (customElements.get('ac-home-rail')) return;
  class ACHomeRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ac-home-rail__track');
      this.range = this.querySelector('.ac-home-rail__range');
      if (!this.track || !this.range) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.range.addEventListener('input', () => {
        this.track.scrollLeft = (this.track.scrollWidth - this.track.clientWidth) * Number(this.range.value) / 100;
      }, options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.track.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * this.track.clientWidth * 0.7,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      this.measuredWidth = null;
      this.observer = new ResizeObserver(() => {
        this.update();
        if (this.measuredWidth !== this.track.clientWidth) {
          this.measuredWidth = this.track.clientWidth;
          this.alignProductText();
        }
      });
      this.observer.observe(this.track);
      this.track.querySelectorAll('img').forEach(img => img.addEventListener('load', () => {
        this.update();
        this.alignProductText();
      }, options));
      this.update();
      this.alignProductText();
      document.fonts.ready.then(() => { if (this.isConnected) this.alignProductText(); });
    }
    alignProductText() {
      if (this.closest('.ac-home-products--art')) {
        this.alignArtProductText();
        return;
      }
      if (!this.closest('.ac-home-products--craft,.ac-home-products--arrivals')) return;
      const cards = [...this.track.querySelectorAll('.ac-home-product')];
      const rows = cards.map(card => [
        card.querySelector('.ac-home-product__title'),
        card.querySelectorAll('.ac-home-product__meta')[0],
        card.querySelectorAll('.ac-home-product__meta')[1],
        card.querySelector('.ac-home-product__price')
      ]);
      // Transforms preserve the cards' natural heights and every image position.
      rows.flat().filter(Boolean).forEach(node => node.style.removeProperty('--ac-text-shift'));
      this.track.style.removeProperty('--ac-text-space');
      this.range.style.removeProperty('margin-top');
      const trackBottom = this.track.getBoundingClientRect().bottom;
      const rangeGap = parseFloat(getComputedStyle(this.range).marginTop);
      const measured = rows.map(row => row.map(node => node ? {
        node, rect: node.getBoundingClientRect(),
        marginTop: parseFloat(getComputedStyle(node).marginTop) || 0,
        marginBottom: parseFloat(getComputedStyle(node).marginBottom) || 0
      } : null));
      const shifts = [];
      let previousBottom = -Infinity;
      let previousMargin = 0;
      for (let index = 0; index < 4; index++) {
        const cells = measured.map(row => row[index]).filter(Boolean);
        if (!cells.length) continue;
        const top = Math.max(...cells.map(cell => cell.rect.top),
          previousBottom + Math.max(previousMargin, ...cells.map(cell => cell.marginTop)));
        cells.forEach(cell => shifts.push([cell.node, top - cell.rect.top]));
        previousBottom = top + Math.max(...cells.map(cell => cell.rect.height));
        previousMargin = Math.max(...cells.map(cell => cell.marginBottom));
      }
      const extra = Math.max(0, previousBottom - trackBottom);
      shifts.forEach(([node, shift]) => node.style.setProperty('--ac-text-shift', `${shift}px`));
      this.track.style.setProperty('--ac-text-space', `${extra}px`);
      this.range.style.marginTop = `${Math.max(0, rangeGap - extra)}px`;
    }
    alignArtProductText() {
      const properties = ['--ac-art-title-row', '--ac-art-vendor-row', '--ac-art-type-row', '--ac-art-dimensions-row'];
      properties.forEach(property => this.track.style.removeProperty(property));
      const rows = [...this.track.querySelectorAll('.ac-home-product')].map(card => {
        const metadata = card.querySelectorAll('.ac-home-product__meta');
        const nodes = [card.querySelector('.ac-home-product__title'), metadata[0], metadata[1], metadata[2],
          card.querySelector('.ac-home-product__price')];
        // Reserve absent metadata rows so every price starts on the same baseline.
        nodes.forEach((node, index) => { if (node) node.style.gridRow = String(index + 2); });
        return nodes;
      });
      const heights = properties.map((property, index) => Math.max(0, ...rows.map(row => {
        const node = row[index];
        if (!node) return 0;
        const style = getComputedStyle(node);
        return node.getBoundingClientRect().height +
          (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      })));
      properties.forEach((property, index) => this.track.style.setProperty(property, `${heights[index]}px`));
    }
    update() {
      const overflow = this.track.scrollWidth - this.track.clientWidth;
      this.range.value = overflow > 1 ? String(Math.round(this.track.scrollLeft / overflow * 100)) : '0';
      this.range.disabled = overflow <= 1;
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  customElements.define('ac-home-rail', ACHomeRail);
})();
